import {apiGet, apiPost} from './client'

/* Журнал правок и откат.
 *
 * Gramps Web ведёт его сам, в отдельной базе undo.db рядом с деревом:
 * каждая правка через API попадает туда с именем того, кто её сделал.
 * Прямые правки в базе (наши разовые скрипты) в журнал не попадают —
 * там видно только то, что шло через интерфейс.
 *
 * Права: читать журнал может любой от Member, а откатывать — только
 * Editor и выше. Поэтому список мы прячем у себя, а откат защищён
 * ещё и на стороне сервера.
 */

/** Роль пользователя, как их нумерует Gramps. */
export const ROLE = {
  guest: 0, member: 1, contributor: 2, editor: 3, owner: 4, admin: 5,
} as const

export interface Me {
  name: string
  full_name?: string
  email?: string
  role: number
}

export const whoami = () => apiGet<Me>('/users/-/')

export interface Change {
  obj_class: string
  obj_handle: string
  /** 0 — создано, 1 — изменено, 2 — удалено */
  trans_type: number
  /* Объект целиком до и после правки. Приходят только по запросу: в общем
     списке их нет, иначе на каждую страницу журнала уезжали бы сотни
     килобайт снимков, которые почти всегда не разворачивают. */
  old_data?: Record<string, unknown> | null
  new_data?: Record<string, unknown> | null
}

export interface Txn {
  id: number
  /* Сервер выкидывает внутренний user_id и кладёт в поле user ОБЪЕКТ
     с логином и полным именем. Я сперва читал user_id (графа пустовала),
     потом вывел user как текст — React на объекте в разметке падает,
     и страница гасла целиком. */
  connection?: {user?: {name?: string; full_name?: string} | null; timestamp?: number}
  description?: string
  /** секунды с начала эпохи: в базе наносекунды, но сервер делит сам */
  timestamp: number
  undo?: boolean
  changes?: Change[]
}

export const history = (page = 1, pagesize = 40) =>
  apiGet<Txn[]>('/transactions/history/', {page, pagesize, sort: '-id'})

/* Входы. Штатный Gramps Web их не пишет вовсе — ни в базу пользователей,
   ни в API. Записывает наш слой поверх образа (backend-overlay/): он ловит
   POST /api/token/ уже внутри приложения, где известно имя и не нужно
   трогать ни пароль, ни выданный токен. */
export interface Login {
  /** секунды с начала эпохи */
  ts: number
  username: string
  ok: boolean
  ip: string
  agent: string
  /** 'app' — записали мы и знаем имя; 'proxy' — добрано из логов прокси,
   *  имени там нет: логин лежит в теле запроса, а тела прокси не пишет. */
  src?: 'app' | 'proxy'
}

export const logins = (limit = 200) => apiGet<Login[]>('/logins/', {limit})

/* Просмотры карточек. Считаются на СЕРВЕРЕ, по запросам к /api/people/<handle>:
   так учитывается и штатный интерфейс Gramps, и наш, а клиент не может
   «забыть» сообщить о просмотре. Повтор одной карточки в пределах получаса
   за новый просмотр не считается — вкладку перечитали, правку сохранили,
   вернулись назад: это один интерес, а не десять. */
export interface View {
  ts: number
  username: string
  /** handle человека, чью карточку открыли */
  person: string
}

export const viewsLog = (limit = 300) => apiGet<View[]>('/views/', {limit})

/** Одна правка со снимками «до» и «после» — для разбора по полям. */
export const historyOne = (id: number) =>
  apiGet<Txn>(`/transactions/history/${id}`, {old: 1, new: 1})

export const undo = (id: number) =>
  apiPost<unknown>(`/transactions/history/${id}/undo`, {})

/* Тип объекта приходит то словом («Media»), то внутренним числовым кодом
   Gramps. Держим оба, иначе в журнале вылезает «7 — создано». */
const CLASS_RU: Record<string, string> = {
  Person: 'человек', Family: 'семья', Event: 'событие', Place: 'место',
  Media: 'снимок', Note: 'заметка', Citation: 'цитата', Source: 'источник',
  Repository: 'архив', Tag: 'метка',
  '0': 'человек', '1': 'семья', '2': 'источник', '3': 'событие', '4': 'снимок',
  '5': 'место', '6': 'архив', '8': 'заметка', '9': 'метка', '10': 'цитата',
}

/** Служебная запись о перестройке ссылок — показывать её незачем. */
export const isPlumbing = (c: Change) =>
  c.obj_class === '7' || c.obj_class === 'Reference'

export const KIND_RU = ['создано', 'изменено', 'удалено']

export const classLabel = (c: Change): string => CLASS_RU[c.obj_class] || c.obj_class

/** Описания приходят от Gramps по-английски и довольно однообразные. */
const DESC_RU: Record<string, string> = {
  'Edit Person': 'Правка человека',
  'Add Person': 'Новый человек',
  'Delete Person': 'Удаление человека',
  'Edit Family': 'Правка семьи',
  'Add Family': 'Новая семья',
  'Edit Event': 'Правка события',
  'New Event': 'Новое событие',
  'Add Event': 'Новое событие',
  'Delete Event': 'Удаление события',
  'Edit Place': 'Правка места',
  'New Place': 'Новое место',
  'Add Place': 'Новое место',
  'Edit Media': 'Правка снимка',
  'Add Media': 'Новый снимок',
  'Delete Media': 'Удаление снимка',
  'Edit Note': 'Правка заметки',
  'Add Note': 'Новая заметка',
}

/** Кто правил: полное имя, если заполнено, иначе логин. */
export const userLabel = (t: Txn): string => {
  const u = t.connection?.user
  return (u?.full_name || u?.name || '').trim() || '—'
}

export const descLabel = (t: Txn): string => {
  const d = (t.description || '').trim()
  return DESC_RU[d] || d || 'Правка'
}
